import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, CheckCircle2, AlertCircle, X, Info, ChevronLeft, ChevronRight, Upload, Image as ImageIcon } from 'lucide-react';
import { productService } from '../services/api.service';
import { getProductImageUrl } from '../../services/api';

const ITEMS_PER_PAGE = 10;

// Completely blank initial form state for Add New Panel
const BLANK_FORM_DATA = {
  name: '',
  sku: '',
  price: '',
  sale_price: '',
  stock: '',
  description: '',
  image_url: '',
  led_chips_count: '',
  actual_power: '',
  working_current: '',
  input_voltage: '100-240V AC',
  frequency: '50/60Hz',
  lens_angle: '30°60°',
  irradiance: '',
  dimensions: '',
  spectrum: '',
  control_interface: 'Touch Screen control interface',
  flicker: 'support 1-10000Hz with individual wavelength adjustable',
  dimmer: '1 ~ 100% with individual wavelength adjustable',
  shell_material: 'Iron',
  shell_color: 'White',
  package_includes: '1pc light + 1pc wire with plug + 1pc manual + 1pc glasses',
  weight_kg: '',
  remote_controller: '2.4G Remote Controller: Support',
  optional_addons: [],
};

const Panels = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [specDrawerItem, setSpecDrawerItem] = useState(null);
  const [galleryModalProduct, setGalleryModalProduct] = useState(null);
  const [stagedGalleryFiles, setStagedGalleryFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [formData, setFormData] = useState(BLANK_FORM_DATA);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await productService.getProducts();
      setProducts(res.products || (Array.isArray(res) ? res : (res.data || [])));
    } catch (err) {
      setError('Failed to load products from server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // OPEN ADD DRAWER: Reset form to 100% CLEAN BLANK values
  const handleOpenAddDrawer = () => {
    setEditingId(null);
    setFormData({
      ...BLANK_FORM_DATA,
      sku: `IE-PANEL-${Math.floor(100 + Math.random() * 900)}`,
      image_url: '',
    });
    setError('');
    setDrawerOpen(true);
  };

  // OPEN EDIT DRAWER: Pre-fill with selected panel's exact database values
  const handleOpenEditDrawer = (p) => {
    setEditingId(p.id);
    setFormData({
      name: p.name || '',
      sku: p.sku || '',
      price: p.price || '',
      sale_price: p.sale_price || '',
      stock: p.stock || '',
      description: p.description || '',
      image_url: p.image_url || '/uploads/panel_72led.jpg',
      led_chips_count: p.led_chips_count || '',
      actual_power: p.actual_power || '',
      working_current: p.working_current || '2.00A ± 5%/220V',
      frequency: p.frequency || '50/60Hz',
      lens_angle: p.lens_angle || '30°60°',
      irradiance: p.irradiance || '',
      dimensions: p.dimensions || '',
      spectrum: p.spectrum || '',
      control_interface: p.control_interface || 'Touch Screen control interface',
      flicker: p.flicker || 'support 1-10000Hz',
      dimmer: p.dimmer || '1 ~ 100%',
      shell_material: p.shell_material || 'Iron',
      shell_color: p.shell_color || 'White',
      package_includes: p.package_includes || '1pc light + 1pc wire with plug + 1pc manual + 1pc glasses',
      weight_kg: p.weight_kg || '',
      remote_controller: p.remote_controller || '2.4G Remote Controller: Support',
      input_voltage: p.input_voltage || '100-240V AC',
      optional_addons: Array.isArray(p.optional_addons)
        ? p.optional_addons.map(a => ({ addon_name: a.addon_name || '', addon_price: a.addon_price ?? '' }))
        : [],
    });
    setError('');
    setDrawerOpen(true);
  };

  // Add-on row helpers (the add-on table supports single OR multiple rows per product)
  const handleAddonChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.optional_addons];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, optional_addons: next };
    });
  };

  const handleAddAddonRow = () => {
    setFormData(prev => ({
      ...prev,
      optional_addons: [...prev.optional_addons, { addon_name: '', addon_price: '' }]
    }));
  };

  const handleRemoveAddonRow = (index) => {
    setFormData(prev => ({
      ...prev,
      optional_addons: prev.optional_addons.filter((_, i) => i !== index)
    }));
  };

  // Upload image from device gallery
  const handleGalleryFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (PNG, JPG, WEBP).');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setUploadingImg(true);
        setError('');
        const res = await productService.uploadImage(reader.result, file.name);
        if (res.imageUrl) {
          setFormData(prev => ({ ...prev, image_url: res.imageUrl }));
          setSuccessMsg('Gallery image uploaded to backend server!');
        }
      } catch (err) {
        setError(err.message || 'Failed to upload image from gallery.');
      } finally {
        setUploadingImg(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Staged Preview Files Selection (No automatic upload!)
  const handleSelectFilesForStaging = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newStaged = [];
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        newStaged.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          previewUrl: URL.createObjectURL(file)
        });
      }
    });

    setStagedGalleryFiles(prev => [...prev, ...newStaged]);
  };

  const handleRemoveStagedFile = (id) => {
    setStagedGalleryFiles(prev => prev.filter(item => item.id !== id));
  };

  // Explicit Upload Button Action
  const handleUploadStagedGalleryImages = async () => {
    if (stagedGalleryFiles.length === 0 || !galleryModalProduct) return;

    setUploadingGallery(true);
    setError('');
    try {
      const uploadedUrls = [];
      for (const item of stagedGalleryFiles) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(item.file);
        });
        const base64Data = await base64Promise;
        const res = await productService.uploadImage(base64Data, item.file.name);
        if (res.imageUrl) {
          uploadedUrls.push(res.imageUrl);
        }
      }

      const currentGallery = galleryModalProduct.gallery_images || [];
      const newGallery = [...currentGallery, ...uploadedUrls];

      const updated = await productService.updateProduct(galleryModalProduct.id, {
        ...galleryModalProduct,
        gallery_images: newGallery
      });

      const updatedProduct = updated?.product || (updated?.id ? updated : { ...galleryModalProduct, gallery_images: newGallery });

      setGalleryModalProduct(updatedProduct);
      setStagedGalleryFiles([]);
      fetchProducts();
      setSuccessMsg('Selected gallery images uploaded successfully!');
    } catch (err) {
      setError('Failed uploading gallery images: ' + err.message);
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleDeleteGalleryImage = async (imgUrlToDelete) => {
    if (!galleryModalProduct) return;
    try {
      const currentGallery = galleryModalProduct.gallery_images || [];
      const updatedGallery = currentGallery.filter(url => url !== imgUrlToDelete);

      const updated = await productService.updateProduct(galleryModalProduct.id, {
        ...galleryModalProduct,
        gallery_images: updatedGallery
      });

      const updatedProduct = updated?.product || (updated?.id ? updated : { ...galleryModalProduct, gallery_images: updatedGallery });

      setGalleryModalProduct(updatedProduct);
      fetchProducts();
    } catch (err) {
      setError('Failed to delete gallery image: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this red light panel?')) {
      try {
        await productService.deleteProduct(id);
        setSuccessMsg('Product deleted successfully.');
        fetchProducts();
      } catch (err) {
        setError('Failed to delete product.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price || !formData.sku) {
      setError('Product name, SKU, and price are required.');
      return;
    }

    try {
      setError('');
      if (editingId) {
        await productService.updateProduct(editingId, formData);
        setSuccessMsg('Product updated successfully.');
      } else {
        await productService.addProduct(formData);
        setSuccessMsg('New panel product created successfully.');
      }
      setDrawerOpen(false);
      fetchProducts();
    } catch (err) {
      setError(err.message || 'Failed to save product.');
    }
  };

  const filteredProducts = (products || []).filter(p =>
    (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE) || 1;
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Top Banner & Action */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>Red Light Panel Catalog ({products.length} Models)</h2>
          <p style={{ fontSize: '13px', color: '#64748b' }}>Complete technical specifications from official product brochure (LEDs, Power, Irradiance, Dimensions, Spectrum).</p>
        </div>

        <button onClick={handleOpenAddDrawer} className="btn-red-submit" style={{ width: 'auto', padding: '0 20px', height: '42px', fontSize: '13px' }}>
          <Plus size={18} />
          <span>Add New Panel</span>
        </button>
      </div>

      {successMsg && (
        <div className="form-alert" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' }}>
          <CheckCircle2 size={16} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Filter & Search Bar */}
      <div className="table-card-box">
        <div className="card-header-flex" style={{ marginBottom: '16px' }}>
          <div className="header-search-wrapper" style={{ width: '360px', display: 'block' }}>
            <input
              type="text"
              className="header-search-input"
              placeholder="Search panels by name, SKU or specs..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            <Search className="header-search-icon" size={16} />
          </div>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
            Showing {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredProducts.length)} of {filteredProducts.length} Panel Models
          </span>
        </div>

        {/* Product Table */}
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading brochure panel catalog...</div>
        ) : (
          <>
            <table className="custom-data-table">
              <thead>
                <tr>
                  <th>Panel Model</th>
                  <th>SKU</th>
                  <th>Price (£)</th>
                  <th>Stock</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentProducts.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="product-row-left">
                        <div className="product-img-box">
                          <img src={getProductImageUrl(p.image_url)} alt={p.name} onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }} />
                        </div>
                        <div className="product-info-col">
                          <span className="product-item-title">{p.name}</span>
                          <span className="product-item-sales">{p.description}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#334155' }}>{p.sku}</td>
                    <td style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>£{parseFloat(p.price || 0).toFixed(2)}</td>
                    <td>
                      <span className={`badge-pill ${p.stock > 15 ? 'badge-completed' : 'badge-low-stock'}`}>
                        {p.stock > 0 ? `${p.stock} In Stock` : 'Out of Stock'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        {/* Dedicated Product Gallery Icon Button with Hover Tooltip */}
                        <button
                          onClick={() => { setGalleryModalProduct(p); setStagedGalleryFiles([]); }}
                          title={`Gallery Images (${p.gallery_images ? p.gallery_images.length : 0})`}
                          style={{ background: '#fef3c7', border: '1px solid #fde68a', padding: '6px', borderRadius: '6px', cursor: 'pointer', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <ImageIcon size={15} />
                        </button>

                        <button onClick={() => setSpecDrawerItem(p)} title="View Technical Specs" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '6px', borderRadius: '6px', cursor: 'pointer', color: '#16a34a' }}>
                          <Info size={15} />
                        </button>
                        <button onClick={() => handleOpenEditDrawer(p)} title="Edit Panel" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '6px', borderRadius: '6px', cursor: 'pointer', color: '#334155' }}>
                          <Edit2 size={15} />
                        </button>
                        <button onClick={() => handleDelete(p.id)} title="Delete Panel" style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '6px', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                  Page {currentPage} of {totalPages}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
                  >
                    <ChevronLeft size={16} />
                    <span>Previous</span>
                  </button>

                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      style={{ width: '32px', height: '32px', border: '1px solid', borderColor: currentPage === idx + 1 ? '#dc2626' : '#e2e8f0', borderRadius: '6px', background: currentPage === idx + 1 ? '#dc2626' : '#ffffff', color: currentPage === idx + 1 ? '#ffffff' : '#334155', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                    >
                      {idx + 1}
                    </button>
                  ))}

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
                  >
                    <span>Next</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* DEDICATED PRODUCT GALLERY MODAL */}
      {galleryModalProduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', maxWidth: '740px', width: '100%', padding: '32px', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            
            <button
              onClick={() => { setGalleryModalProduct(null); setStagedGalleryFiles([]); }}
              style={{ position: 'absolute', top: '20px', right: '20px', background: '#f1f5f9', border: 'none', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#0f172a' }}
            >
              <X size={20} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ImageIcon size={20} />
              </div>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', margin: 0 }}>
                  Product Gallery Images
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  Model: <strong>{galleryModalProduct.name}</strong> ({galleryModalProduct.sku})
                </p>
              </div>
            </div>

            {/* Step 1: Select Images Button */}
            <div style={{ border: '2px dashed #cbd5e1', borderRadius: '16px', padding: '20px', background: '#f8fafc', margin: '20px 0', textAlign: 'center' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#334155', color: '#ffffff', padding: '12px 24px', borderRadius: '9999px', fontSize: '13.5px', fontWeight: 800, cursor: 'pointer' }}>
                <ImageIcon size={16} />
                <span>+ Select Images from Device</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleSelectFilesForStaging}
                  style={{ display: 'none' }}
                />
              </label>
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', marginBottom: 0 }}>
                Select photos to preview before confirming your upload.
              </p>
            </div>

            {/* Step 2: Staged Preview Box */}
            {stagedGalleryFiles.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: '16px', padding: '16px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: '#dc2626' }}>
                    Selected Images Preview ({stagedGalleryFiles.length} ready to upload)
                  </span>
                  <button
                    onClick={() => setStagedGalleryFiles([])}
                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Clear All
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {stagedGalleryFiles.map(item => (
                    <div key={item.id} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '10px', border: '1px solid #cbd5e1', overflow: 'hidden', background: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
                      <img src={item.previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        onClick={() => handleRemoveStagedFile(item.id)}
                        title="Remove from preview"
                        style={{ position: 'absolute', top: '3px', right: '3px', background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '10px', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleUploadStagedGalleryImages}
                  disabled={uploadingGallery}
                  style={{
                    width: '100%',
                    background: '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                  }}
                >
                  <Upload size={16} />
                  <span>{uploadingGallery ? 'Uploading to Server...' : `Upload ${stagedGalleryFiles.length} Selected Images`}</span>
                </button>
              </div>
            )}

            {/* Published Gallery Images */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', marginBottom: '12px' }}>
                Published Gallery Images ({galleryModalProduct.gallery_images ? galleryModalProduct.gallery_images.length : 0})
              </h4>

              {(!galleryModalProduct.gallery_images || galleryModalProduct.gallery_images.length === 0) ? (
                <div style={{ padding: '24px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                  No secondary gallery images published yet for this model.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', maxHeight: '240px', overflowY: 'auto', paddingRight: '4px' }}>
                  {galleryModalProduct.gallery_images.map((imgUrl, idx) => (
                    <div key={idx} style={{ position: 'relative', height: '110px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={getProductImageUrl(imgUrl)} alt="Gallery item" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }} />
                      <button
                        onClick={() => handleDeleteGalleryImage(imgUrl)}
                        title="Delete this image"
                        style={{ position: 'absolute', top: '6px', right: '6px', background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', fontSize: '11px', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(220,38,38,0.4)' }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* RIGHT HALF SCREEN SLIDE-OVER DRAWER FOR VIEW SPECS */}
      {specDrawerItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(9, 10, 15, 0.6)', backdropFilter: 'blur(3px)', zIndex: 220 }}>
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '580px', maxWidth: '100%', background: '#ffffff', boxShadow: '-10px 0 30px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{specDrawerItem.name}</h3>
                <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, fontFamily: 'monospace' }}>SKU: {specDrawerItem.sku}</span>
              </div>
              <button onClick={() => setSpecDrawerItem(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ textAlign: 'center', padding: '20px', background: '#090a0f', borderRadius: '12px' }}>
                <img src={getProductImageUrl(specDrawerItem.image_url)} alt={specDrawerItem.name} style={{ maxHeight: '180px', objectFit: 'contain' }} onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>LED CHIPS COUNT</strong>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>{specDrawerItem.led_chips_count}pcs 5W Dual LEDs</span>
                </div>
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>ACTUAL POWER</strong>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>{specDrawerItem.actual_power}</span>
                </div>
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>WORKING CURRENT</strong>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{specDrawerItem.working_current || '2.00A ± 5%/220V'}</span>
                </div>
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>FREQUENCY & LENS ANGLE</strong>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{specDrawerItem.frequency} ({specDrawerItem.lens_angle})</span>
                </div>
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>DIMENSIONS & WEIGHT</strong>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{specDrawerItem.dimensions} ({specDrawerItem.weight_kg} kg)</span>
                </div>
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>SPECTRUM WAVELENGTHS</strong>
                  <span style={{ fontWeight: 700, color: '#dc2626' }}>{specDrawerItem.spectrum}</span>
                </div>
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', gridColumn: 'span 2' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>IRRADIANCE LIGHT DENSITY</strong>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{specDrawerItem.irradiance}</span>
                </div>
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', gridColumn: 'span 2' }}>
                  <strong style={{ color: '#64748b', display: 'block', fontSize: '11px' }}>PACKAGE INCLUDES</strong>
                  <span style={{ color: '#334155' }}>{specDrawerItem.package_includes}</span>
                </div>
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', textAlign: 'right' }}>
              <button onClick={() => setSpecDrawerItem(null)} className="btn-red-submit" style={{ width: 'auto', padding: '0 24px', height: '40px', fontSize: '13px' }}>
                Close Technical Specs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RIGHT HALF SCREEN SLIDE-OVER DRAWER FOR ADD / EDIT PANEL (ALL 22 BROCHURE FIELDS) */}
      {drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(9, 10, 15, 0.6)', backdropFilter: 'blur(3px)', zIndex: 220 }}>
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '620px', maxWidth: '100%', background: '#ffffff', boxShadow: '-10px 0 30px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                {editingId ? 'Edit Panel Specifications' : 'Add New Red Light Therapy Panel'}
              </h3>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="form-alert" style={{ margin: '16px 24px 0' }}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Image Upload Box with Full Image Display & Floating Top-Right Circular Edit Button */}
              <div>
                <label className="input-label">Product Panel Image</label>
                <div style={{ position: 'relative', border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '12px 16px', background: '#090a0f', textAlign: 'center', minHeight: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {formData.image_url ? (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={formData.image_url.startsWith('data:') ? formData.image_url : getProductImageUrl(formData.image_url)}
                        alt="Panel Preview"
                        style={{ width: '100%', maxHeight: '160px', objectFit: 'contain' }}
                        onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }}
                      />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#94a3b8', padding: '8px 0' }}>
                      <ImageIcon size={26} color="#ef4444" />
                      <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#ffffff' }}>Upload Panel Image</span>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>PNG, JPG or WEBP from Gallery</span>
                    </div>
                  )}

                  {/* Circular Edit / Upload Button in Top-Right Corner */}
                  <label
                    title="Change / Upload Image"
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.5)',
                      transition: 'transform 0.2s ease',
                      zIndex: 5
                    }}
                  >
                    <Edit2 size={15} />
                    <input type="file" accept="image/*" onChange={handleGalleryFileSelect} style={{ display: 'none' }} />
                  </label>
                </div>
              </div>

              {/* SECTION 1: BASIC INFORMATION */}
              <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#dc2626', marginBottom: '12px' }}>1. Basic Product Information</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label className="input-label">Panel Title / Model Name</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="e.g. InfraEdge 288pcs Max Panel"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="input-label">SKU Code</label>
                      <input
                        type="text"
                        className="styled-input"
                        placeholder="IE-288P-480W"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="input-label">Price (£ GBP)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="styled-input"
                        placeholder="1400.00"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="input-label">Stock Units</label>
                      <input
                        type="number"
                        min="0"
                        className="styled-input"
                        placeholder="20"
                        value={formData.stock}
                        onChange={(e) => setFormData({ ...formData, stock: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10)) })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="input-label">Short Description</label>
                    <textarea
                      className="styled-input"
                      style={{ height: '60px', padding: '8px 12px' }}
                      placeholder="High-power 288-LED panel designed for deep joint & collagen acceleration..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 2: LED & POWER SPECS (BROCHURE PAGE 4) */}
              <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#dc2626', marginBottom: '12px' }}>2. LED & Electrical Power Specs</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label className="input-label">LED Dual Chips Count</label>
                    <input
                      type="number"
                      className="styled-input"
                      placeholder="288"
                      value={formData.led_chips_count}
                      onChange={(e) => setFormData({ ...formData, led_chips_count: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="input-label">Actual Power (W)</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="480W +/-10%"
                      value={formData.actual_power}
                      onChange={(e) => setFormData({ ...formData, actual_power: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="input-label">Working Current</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="4.0A ± 5%/220V / 6.6 ± 5%/110V"
                      value={formData.working_current}
                      onChange={(e) => setFormData({ ...formData, working_current: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="input-label">Frequency / Lens Angle</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="50/60Hz | Lens: 30°60°"
                      value={formData.frequency}
                      onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 3: SPECTRUM & IRRADIANCE */}
              <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#dc2626', marginBottom: '12px' }}>3. Irradiance & Wavelength Spectrum</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label className="input-label">Irradiance Light Density (W/m²)</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="ALL:6inch ≥ 1400W/m² RED:6inch ≥ 150W/m² NIR:6inch ≥ 1200W/m²"
                      value={formData.irradiance}
                      onChange={(e) => setFormData({ ...formData, irradiance: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="input-label">Spectrum Wavelengths (nm)</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="480,590,630,660,670,810,830,850,1060 nm or customize"
                      value={formData.spectrum}
                      onChange={(e) => setFormData({ ...formData, spectrum: e.target.value })}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="input-label">Flicker Control</label>
                      <input
                        type="text"
                        className="styled-input"
                        placeholder="support 1-10000Hz"
                        value={formData.flicker}
                        onChange={(e) => setFormData({ ...formData, flicker: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="input-label">Dimmer Range</label>
                      <input
                        type="text"
                        className="styled-input"
                        placeholder="1 ~ 100% adjustable"
                        value={formData.dimmer}
                        onChange={(e) => setFormData({ ...formData, dimmer: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 4: BUILD & PACKAGE DETAILS */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#dc2626', marginBottom: '12px' }}>4. Physical Dimensions & Package</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label className="input-label">Dimensions (L*W*H MM)</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="920*300*65MM"
                      value={formData.dimensions}
                      onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="input-label">Gross Weight (KG)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="styled-input"
                      placeholder="13.5"
                      value={formData.weight_kg}
                      onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="input-label">Shell Material</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="Iron"
                      value={formData.shell_material}
                      onChange={(e) => setFormData({ ...formData, shell_material: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="input-label">Shell Color</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="White or customize"
                      value={formData.shell_color}
                      onChange={(e) => setFormData({ ...formData, shell_color: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="input-label">Input Voltage</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="100-240V AC"
                      value={formData.input_voltage}
                      onChange={(e) => setFormData({ ...formData, input_voltage: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="input-label">Remote Controller</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="2.4G Remote Controller: Support"
                      value={formData.remote_controller}
                      onChange={(e) => setFormData({ ...formData, remote_controller: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label className="input-label">Package Included Items</label>
                    <input
                      type="text"
                      className="styled-input"
                      placeholder="1pc light + 1pc wire with plug + 1pc manual + 1pc glasses"
                      value={formData.package_includes}
                      onChange={(e) => setFormData({ ...formData, package_includes: e.target.value })}
                    />
                  </div>

                </div>
              </div>

              {/* SECTION 5: OPTIONAL ADD-ONS TABLE (single or multiple extra-cost items) */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#dc2626', marginBottom: '12px' }}>5. Optional Add-ons (Extra Cost)</h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {formData.optional_addons.map((addon, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 36px', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        className="styled-input"
                        placeholder="Add-on name (e.g. Touch Screen 2.4G Remote Controller)"
                        value={addon.addon_name}
                        onChange={(e) => handleAddonChange(idx, 'addon_name', e.target.value)}
                      />
                      <input
                        type="number"
                        step="0.01"
                        className="styled-input"
                        placeholder="Price £"
                        value={addon.addon_price}
                        onChange={(e) => handleAddonChange(idx, 'addon_price', e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveAddonRow(idx)}
                        title="Remove this add-on"
                        style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', width: '36px', height: '36px', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={handleAddAddonRow}
                    style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '8px 14px', fontSize: '12.5px', fontWeight: 700, color: '#334155', cursor: 'pointer' }}
                  >
                    <Plus size={14} />
                    <span>Add another add-on</span>
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <button type="button" onClick={() => setDrawerOpen(false)} style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#ffffff', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" className="btn-red-submit" style={{ width: 'auto', padding: '0 24px', height: '40px', fontSize: '13px' }}>
                  Save Panel Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Panels;